export type SednaComposerResult = 'sent' | 'discarded' | 'not-sent';

export interface SednaPopupHandle {
  blocked: boolean;
  close: () => void;
  navigate: (url: string, onResult?: (result: SednaComposerResult) => void) => boolean;
}

const COMPOSER_RESULTS: SednaComposerResult[] = ['sent', 'discarded', 'not-sent'];
const POPUP_FEATURES = 'width=1200,height=2000';

function watchComposerResult(popup: Window, url: string, onResult?: (result: SednaComposerResult) => void): () => void {
  const expectedOrigin = new URL(url).origin;
  let closePoll: ReturnType<typeof window.setInterval> | undefined;

  const cleanup = () => {
    // The listener and cleanup functions intentionally reference each other as one lifecycle pair.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    window.removeEventListener('message', onMessage);
    if (closePoll !== undefined) {
      window.clearInterval(closePoll);
      closePoll = undefined;
    }
  };

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== expectedOrigin || event.source !== popup || !COMPOSER_RESULTS.includes(event.data)) {
      return;
    }

    cleanup();
    onResult?.(event.data);
  };

  window.addEventListener('message', onMessage);
  closePoll = window.setInterval(() => {
    if (popup.closed) {
      cleanup();
    }
  }, 500);

  return cleanup;
}

export function reserveSednaPopup(): SednaPopupHandle {
  const popup = window.open('', '_blank', POPUP_FEATURES);
  let cleanup: (() => void) | undefined;

  return {
    blocked: popup === null,
    close: () => {
      cleanup?.();
      cleanup = undefined;
      if (popup && !popup.closed) {
        popup.close();
      }
    },
    navigate: (url, onResult) => {
      if (!popup || popup.closed) {
        return false;
      }

      cleanup?.();
      cleanup = watchComposerResult(popup, url, onResult);
      popup.location.href = url;
      return true;
    },
  };
}

export function openSednaComposer(url: string, onResult?: (result: SednaComposerResult) => void): SednaPopupHandle {
  const handle = reserveSednaPopup();
  if (!handle.blocked) {
    handle.navigate(url, onResult);
  }
  return handle;
}

export function openSednaReader(url: string, compactMode: boolean): Window | null {
  return compactMode ? window.open(url, '_blank', POPUP_FEATURES) : window.open(url, '_blank');
}

export function buildSednaUrl(
  configuredUrl: string,
  path: string,
  options: { compactMode?: boolean; teamId?: string; searchParams?: Record<string, string> } = {}
): string {
  const url = new URL(configuredUrl);
  url.pathname = path;
  url.hash = '';

  if (options.teamId) {
    url.searchParams.set('teamId', options.teamId);
  }
  Object.entries(options.searchParams ?? {}).forEach(([key, value]) => url.searchParams.set(key, value));
  if (options.compactMode) {
    url.searchParams.set('compact', 'true');
  }

  return url.toString();
}
